import React, { useEffect, useState, useRef } from 'react';
import ThemeToggle from './components/ThemeToggle';
import LanguageToggle from './components/LanguageToggle';
import { useNavigate } from 'react-router-dom';
import { AiOutlineLock, AiOutlineMessage, AiOutlinePhone, AiOutlineAudio } from 'react-icons/ai';
import { isAuthenticated, fetchUserProfile, useLogout, useOutsideClick } from './common';
import { useT } from './LocaleContext';
import { GlobalStyle, DropdownItem, LogoutButton, MenuButton, DropdownMenu } from './styles/GlobalStyles';
import {
  HomePageContainer,
  Title,
  Subtitle,
  LinksList,
  StyledLink,
  WorkspaceLink,
  FeaturesContainer,
  FeatureCard,
  FeatureIcon,
  FeatureTitle,
  FeatureDescription,
  VersionBadge,
} from './styles/HomePageStyles';

const FEATURES = [
  {
    key: 'chat',
    tone: 'chat',
    delay: 80,
    Icon: AiOutlineMessage,
    titleKey: 'home.featureChatTitle',
    descKey: 'home.featureChatDesc',
  },
  {
    key: 'call',
    tone: 'call',
    delay: 140,
    Icon: AiOutlinePhone,
    titleKey: 'home.featureCallTitle',
    descKey: 'home.featureCallDesc',
  },
  {
    key: 'rooms',
    tone: 'rooms',
    delay: 200,
    Icon: AiOutlineAudio,
    titleKey: 'home.featureRoomsTitle',
    descKey: 'home.featureRoomsDesc',
  },
  {
    key: 'e2e',
    tone: 'e2e',
    delay: 260,
    Icon: AiOutlineLock,
    titleKey: 'home.featureE2eTitle',
    descKey: 'home.featureE2eDesc',
  },
];

const getAppVersion = () => {
  if (typeof process !== 'undefined' && process.env && process.env.REACT_APP_PUBLIC_APP_VERSION && process.env.REACT_APP_PUBLIC_APP_VERSION.trim()) {
    return process.env.REACT_APP_PUBLIC_APP_VERSION;
  }

  if (typeof process !== 'undefined' && process.env && process.env.PUBLIC_APP_VERSION && process.env.PUBLIC_APP_VERSION.trim()) {
    return process.env.PUBLIC_APP_VERSION;
  }

  if (typeof window !== 'undefined' && window.PUBLIC_APP_VERSION) {
    return window.PUBLIC_APP_VERSION;
  }

  return '1.0.0';
};

const HomePage = () => {
  const [email, setEmail] = useState(null);
  const [isAuth, setIsAuth] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const handleLogout = useLogout();
  const dropdownRef = useRef(null);
  const t = useT();

  useEffect(() => {
    const checkAuthAndGetUser = async () => {
      try {
        const authenticated = await isAuthenticated();
        setIsAuth(authenticated);

        if (authenticated) {
          try {
            const userData = await fetchUserProfile();
            setEmail(userData.email);
            setIsAdmin(Boolean(userData.is_admin));
          } catch (error) {
            if (error.response) {
              setIsAuth(false);
              setIsAdmin(false);
            }
          }
        }
      } catch (error) {
        if (error.response) {
          setIsAuth(false);
          setIsAdmin(false);
        }
      }
    };

    checkAuthAndGetUser();
  }, [navigate]);

  useOutsideClick(dropdownRef, () => setMenuOpen(false));
  const toggleMenu = () => { setMenuOpen((prev) => !prev); };

  const emailInitials = email ? email.slice(0, 2).toUpperCase() : '';

  return (
    <HomePageContainer>
      <GlobalStyle />
      {isAuth ? (
        <>
          <MenuButton onClick={toggleMenu} $open={menuOpen}>
            {emailInitials || '≡'}
          </MenuButton>
          <DropdownMenu ref={dropdownRef} open={menuOpen}>
            <DropdownItem to="/chats">{t('nav.chats')}</DropdownItem>
            <DropdownItem to="/profile">{t('nav.profile')}</DropdownItem>
            {isAdmin && <DropdownItem to="/admin">{t('nav.adminPanel')}</DropdownItem>}
            <ThemeToggle />
            <LanguageToggle />
            <LogoutButton onClick={handleLogout}>{t('nav.logout')}</LogoutButton>
          </DropdownMenu>
        </>
      ) : (
        <>
          <MenuButton onClick={toggleMenu} $open={menuOpen}>≡</MenuButton>
          <DropdownMenu ref={dropdownRef} open={menuOpen}>
            <DropdownItem to="/login">{t('nav.login')}</DropdownItem>
            <DropdownItem to="/register">{t('nav.register')}</DropdownItem>
            <ThemeToggle />
            <LanguageToggle />
          </DropdownMenu>
        </>
      )}

      <Title>LYNX</Title>
      <Subtitle>{t('home.subtitle')}</Subtitle>

      <FeaturesContainer>
        {FEATURES.map(({ key, tone, delay, Icon, titleKey, descKey }) => (
          <FeatureCard key={key} $tone={tone} $delay={delay}>
            <FeatureIcon $tone={tone}>
              <Icon />
            </FeatureIcon>
            <FeatureTitle $tone={tone}>{t(titleKey)}</FeatureTitle>
            <FeatureDescription>{t(descKey)}</FeatureDescription>
          </FeatureCard>
        ))}
      </FeaturesContainer>

      {isAuth ? (
        <LinksList>
          <li>
            <WorkspaceLink to="/chats">{t('home.openChats')}</WorkspaceLink>
          </li>
        </LinksList>
      ) : (
        <LinksList>
          <li>
            <StyledLink to="/login">{t('home.signIn')}</StyledLink>
          </li>
          <li>
            <StyledLink to="/register">{t('home.signUp')}</StyledLink>
          </li>
        </LinksList>
      )}
      <VersionBadge>
        {t('home.version', { version: getAppVersion() })}
      </VersionBadge>
    </HomePageContainer>
  );
};

export default HomePage;
